/**
 * あおぞらワールドのサーバー権威 API クライアント — docs/21 §5。
 *
 * **移動も攻撃も毎回 edge Worker が権威処理する**ためのクライアント側呼び出し。ユーザー自身の
 * service auth JWT (aud=edge Worker) を発行して edge に渡す。edge が本人確認 → 権威 state を読み書き
 * → 結果を返す。**クライアントは結果を描画するだけで、権威データ (パワー/XP/素材/位置) を自分の PDS
 * に書かない** = 改造してもチートできない。
 */
import { LXM, type AllyMode, type GearSelection, type PartyRecruit, type PlacedKind, type PlacedReward, type RecruitCandidate, type StoryEffect } from '@aozoraquest/core';
import type { Agent } from '@atproto/api';

// エッジ URL/DID の環境別解決は edge-config に一元化 (world/連携が同じエッジを使う。#396)。
import { EDGE_URL, EDGE_DID } from './edge-config';
import type { AllyOrderInput } from './battle-ally-orders';

const LXM_MOVE = LXM.worldMove;
const LXM_TELEPORT = LXM.worldTeleport;
const LXM_ITEM = LXM.worldItem;
const LXM_SKILL = LXM.worldSkill;
const LXM_GEAR = LXM.worldGear;
const LXM_SEARCH = LXM.worldSearch;
const LXM_RESET = LXM.worldReset;
const LXM_TURN = LXM.battleTurn;
const LXM_STATE = LXM.meState;
const LXM_XP_CLAIM = LXM.xpClaim;
const LXM_XP_ADMIN_SET = LXM.xpAdminSet;
const LXM_POWER_ADMIN_GRANT = LXM.powerAdminGrant;
const LXM_SHOP_CRAFT = LXM.shopCraft;
const LXM_SHOP_SELL = LXM.shopSell;
const LXM_SHOP_BUY = LXM.shopBuy;
const LXM_SHOP_FORGE = LXM.shopForge;
const LXM_SHOP_DISCARD = LXM.shopDiscard;
const LXM_QUEST_ACCEPT = LXM.questAccept;
const LXM_QUEST_COMPLETE = LXM.questComplete;
const LXM_STORY_BATTLE = LXM.storyBattle;
const LXM_INN_REST = LXM.innRest;
const LXM_PARTY_SET = LXM.partySet;
const LXM_PARTY_CANDIDATES = LXM.partyCandidates;
const LXM_PARTY_REROLL = LXM.partyReroll;
const LXM_PARTY_MODE = LXM.partyMode;
const LXM_PARTY_GEAR = LXM.partyGear;
const LXM_POWER_SPEND = LXM.powerSpend;
const LXM_ADMIN_PDS_USAGE = LXM.adminPdsUsage;

/** edge URL / DID が設定されていればサーバー権威モードを使える。 */
export const worldServerEnabled = Boolean(EDGE_URL && EDGE_DID);

/** サーバー権威エンドポイントのエラー (status 付き。503=書込不能で報酬なし fail-closed 等)。 */
export class WorldServerError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
  }
}

/** edge への 1 リクエストのタイムアウト (ms)。応答が返らずハングしても busy が永久固着しないよう、
 *  必ず fail-closed で throw させる (通信断/無応答でも報酬は付かない・クライアント権威にフォールバックしない)。 */
const EDGE_TIMEOUT_MS = 8000;

/** service auth JWT を lxm ごとにキャッシュ (毎アクション PDS 往復を避ける。exp 30s 前まで再利用)。 */
const tokenCache = new Map<string, { token: string; exp: number }>();

async function serviceToken(agent: Agent, lxm: string): Promise<string> {
  const nowSec = Date.now() / 1000;
  const cached = tokenCache.get(lxm);
  if (cached && cached.exp - 30 > nowSec) return cached.token;
  const { data } = await agent.com.atproto.server.getServiceAuth({ aud: EDGE_DID!, lxm });
  let exp = nowSec + 60;
  try {
    exp = (JSON.parse(atob(data.token.split('.')[1]!)) as { exp?: number }).exp ?? exp;
  } catch { /* exp 取れなければ 60s 後 */ }
  tokenCache.set(lxm, { token: data.token, exp });
  return data.token;
}

async function callEdge<T>(agent: Agent, lxm: string, path: string, body: unknown): Promise<T> {
  if (!EDGE_URL || !EDGE_DID) throw new WorldServerError('サーバー権威 API 未設定', 0, 'not_configured');
  const token = await serviceToken(agent, lxm);
  let res: Response;
  try {
    res = await fetch(`${EDGE_URL}${path}`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(EDGE_TIMEOUT_MS),
    });
  } catch (e) {
    // タイムアウト/通信断 = fail-closed (報酬なし)。status 0 で catch 側が busy を必ず解除できるようにする。
    const timedOut = e instanceof DOMException && (e.name === 'TimeoutError' || e.name === 'AbortError');
    throw new WorldServerError(timedOut ? 'サーバーが応答しません' : '通信に失敗しました', 0, timedOut ? 'timeout' : 'network');
  }
  const data = (await res.json().catch(() => ({}))) as T & { error?: string; message?: string; reason?: string };
  if (!res.ok) {
    if (res.status === 401) tokenCache.delete(lxm); // 失効トークンを捨てて次アクションで再取得 (401 ループ回避)
    throw new WorldServerError(data.message ?? data.reason ?? data.error ?? `edge ${res.status}`, res.status, data.error);
  }
  return data as T;
}

// ── DTO (edge の返り値。seed は含まれない = 先読み不可) ──
export interface ServerMonster { name: string; maxHp: number; hp: number; maxMp: number; mp: number; [k: string]: unknown }
export interface ServerBattleState { player: ServerMonster; monster: ServerMonster; monsterId: string; outcome: string; turn: number; lastEvents: { actor: string; text: string }[]; [k: string]: unknown }
export interface ServerEncounter { battleId: string; monsterId: string; state: ServerBattleState; rewarded: boolean }
export interface ServerMoveResult {
  /** 移動後のマップ (#424)。省略 = フィールド。 */
  mapId?: string; x: number; y: number; terrain: string; healed?: boolean; token: string; encounter?: ServerEncounter;
  /** 到着した街のフィールド座標 (内部マップ座標ではない)。探索メモ用。 */
  townArrival?: { x: number; y: number } }
/** 権威 GameState (パワー/XP/素材/位置/carry HP-MP 等)。表示はこれを正とする。 */
/** 所持装備の 1 個体 (#551 段階 2)。**権威側が唯一の正**で、ここに無い個体は装備できない。 */
export interface ServerOwnedPiece { rkey: string; itemId: string; level: number }
/** パーティ (D-PARTY-001)。ids は入れている順 (主人公は含めない)。hp/mp に値の無い仲間は全快。
 *  members は生成の仲間の定義と経験値、xp はなじみの経験値 (D-PARTY-002)。 */
export interface ServerParty { ids: string[]; hp?: Record<string, number>; mp?: Record<string, number>; members?: PartyRecruit[]; xp?: Record<string, number>; bond?: Record<string, number>; mode?: Record<string, AllyMode>; gear?: Record<string, GearSelection> }
/** きずなの ♥ が増えた仲間 (増えたあとの ♥ の数。D-PARTY-004)。 */
export interface ServerBondUp { id: string; name: string; hearts: number }
/** ギルドの今日の候補 (D-PARTY-002)。入れた人は含まない。 */
export interface ServerPartyCandidates { day: string; candidates: RecruitCandidate[]; power: number; rerollPower: number }
export interface ServerGameState { party?: ServerParty; did: string; power: number; playerXp: number; jobXp: Record<string, number>; materials: Record<string, number>; gear: string[]; pieces?: ServerOwnedPiece[]; x: number; y: number; carryHp?: number; carryMp?: number; herbs?: number; tonics?: number; mapId?: string; flags?: string[]; activeQuests: Array<{ id: string; progress: number }>; questsDone?: string[]; openedSpots?: string[]; version: number; updatedAt: string }
export interface ServerStateResult { state: ServerGameState; initialized: boolean; token?: string }
export interface ServerAward {
  /** パワー不足で報酬対象外だった (勝っても逃げても XP・素材が入らない)。 */
  unrewarded?: true;
  xp?: number;
  drops?: string[];
  materialsLost?: string[];
  powerSpent?: number;
  /** この決着でジョブ Lv が上がったか (#534)。上がったら HP/MP が全回復している。 */
  leveledUp?: {
    from: number;
    to: number;
    /** 上がったステータスの内訳 (1 行ずつ出す)。 */
    gains?: Array<{ key: string; label: string; delta: number }>;
    /** 新しく覚えたとくぎの名前。 */
    learned?: string[];
  };
  /** この決着で Lv が上がった仲間 (D-PARTY-002)。上がった仲間は全快している。 */
  allyLeveledUp?: Array<{ id: string; name: string; from: number; to: number }>;
  allyBondUp?: ServerBondUp[];
}
export interface ServerTurnResult { party?: ServerParty; state: ServerBattleState; events: { actor: string; text: string }[]; outcome: string; awarded?: ServerAward; position?: { x: number; y: number }; token?: string; materials?: Record<string, number>; carryHp?: number; carryMp?: number; flags?: string[]; scenarioNotices?: string[]; scenarioMessages?: ScenarioMessage[];
  /** 決着の全受注snapshot（空も明示）。未決着では省略。 */
  activeQuests?: Array<{ id: string; progress: number }>; questsDone?: string[] }
export interface ServerItemResult { carryHp?: number; carryMp?: number; materials: Record<string, number>; healed: number; party?: ServerParty; allyBondUp?: ServerBondUp[] }
/** フィールドの回復のとくぎの結果 (D-PARTY-003)。healed は回復した人ごと (全体回復は全員)。 */
export interface ServerSkillResult { healed: Array<{ id: string; name: string; amount: number }>; casterName: string; skillName: string; carryHp?: number; carryMp?: number; party?: ServerParty; duplicate?: boolean; allyBondUp?: ServerBondUp[] }
export interface ServerTeleportResult { x: number; y: number; token: string; materials: Record<string, number> }

/** 移動: 隣接1マス (dx,dy ∈ {-1,0,1})。位置トークン (署名済み) を渡し、遭遇判定 + 新トークンをサーバーが返す。
 *  token 未指定 (初回) はサーバーが gameState から位置を再同期する。歩行では PDS を触らないので高速。 */
export function serverMove(agent: Agent, dx: number, dy: number, token?: string): Promise<ServerMoveResult> {
  return callEdge<ServerMoveResult>(agent, LXM_MOVE, '/api/world/move', token ? { dx, dy, token } : { dx, dy });
}

/** 攻撃 (1ターン): battleId + turn + command。解決 + 決着報酬はサーバー。 */
export function serverTurn(agent: Agent, battleId: string, turn: number, command: string, skillIndex?: number, itemId?: string, targetIndex?: number, allies?: readonly AllyOrderInput[]): Promise<ServerTurnResult> {
  // skillIndex: とくぎ選択 (#436)。command==='skill' のときどのとくぎか。省略/0 は署名スキル。
  // itemId: command==='item' のときの戦闘用どうぐ (D-ITEM-001)。
  // targetIndex: 群れでねらう敵 (生きている敵の中で何番目か。D-PARTY-001)。省略/0 は先頭。
  return callEdge<ServerTurnResult>(agent, LXM_TURN, '/api/battle/turn', { battleId, turn, command, ...(skillIndex ? { skillIndex } : {}), ...(itemId ? { itemId } : {}), ...(targetIndex ? { targetIndex } : {}), ...(allies?.length ? { allies } : {}) });
}

/** パーティの編成 (D-PARTY-001)。ギルドの受付の隣でだけ通る (位置は署名済みトークンで edge が確かめる)。
 *  payRkey があれば Lv の足りない人をパワーで迎える (D-PARTY-002 §14。rkey は 1 回の押下ごとに 1 つ)。 */
export function serverPartySet(agent: Agent, guild: string, ids: readonly string[], token?: string, payRkey?: string): Promise<{ party: ServerParty; power: number }> {
  return callEdge<{ party: ServerParty; power: number }>(agent, LXM_PARTY_SET, '/api/party', { guild, ids, ...(token ? { token } : {}), ...(payRkey ? { payOverLevel: true, rkey: payRkey } : {}) });
}

/** ギルドの今日の候補 (D-PARTY-002)。受付の隣でだけ通る。 */
export function serverPartyCandidates(agent: Agent, guild: string, token?: string): Promise<ServerPartyCandidates> {
  return callEdge<ServerPartyCandidates>(agent, LXM_PARTY_CANDIDATES, '/api/party/candidates', { guild, ...(token ? { token } : {}) });
}

/** 候補の引き直し (パワーを払う。D-PARTY-002 §10)。rkey は 1 回の押下ごとに 1 つ (再送で二重に引かれない)。 */
export function serverPartyReroll(agent: Agent, guild: string, rkey: string, token?: string): Promise<ServerPartyCandidates> {
  return callEdge<ServerPartyCandidates>(agent, LXM_PARTY_REROLL, '/api/party/reroll', { guild, rkey, ...(token ? { token } : {}) });
}

/** そらのはねワープ: 街 (x,y) へテレポート。位置・トークン・在庫 (そらのはね消費) をサーバーが返す。 */
export function serverTeleport(agent: Agent, x: number, y: number): Promise<ServerTeleportResult> {
  return callEdge<ServerTeleportResult>(agent, LXM_TELEPORT, '/api/world/teleport', { x, y });
}

/** 仲間の さくせん／めいれい (D-PARTY-004)。mode が null なら おまかせ。段階が足りなければ 400 bond_too_low。 */
export function serverPartyMode(agent: Agent, id: string, mode: AllyMode | null): Promise<{ party: ServerParty }> {
  return callEdge<{ party: ServerParty }>(agent, LXM_PARTY_MODE, '/api/party/mode', { id, mode });
}

/** 仲間の装備 (D-PARTY-004)。gear は着る全部 ({id, level})。ほかの人が着ている品は 400 worn_by_other。 */
export function serverPartyGear(agent: Agent, id: string, gear: GearSelection): Promise<{ party: ServerParty }> {
  return callEdge<{ party: ServerParty }>(agent, LXM_PARTY_GEAR, '/api/party/gear', { id, gear });
}

/** フィールドの道具使用: やくそう=herb / そらのしずく=tonic。サーバー在庫を消費して HP/MP を回復。
 *  target は仲間の id (省略は主人公。D-PARTY-003)。 */
export function serverItem(agent: Agent, item: 'herb' | 'tonic', target?: string): Promise<ServerItemResult> {
  return callEdge<ServerItemResult>(agent, LXM_ITEM, '/api/world/item', { item, ...(target ? { target } : {}) });
}

/** フィールドで回復のとくぎを唱える (D-PARTY-003)。caster / target は 'hero' か仲間の id。全体回復は target なし。rkey は 1 回の押下ごとに 1 つ。 */
export function serverFieldSkill(agent: Agent, caster: string, skillId: string, target: string | undefined, rkey: string): Promise<ServerSkillResult> {
  return callEdge<ServerSkillResult>(agent, LXM_SKILL, '/api/world/skill', { caster, skillId, rkey, ...(target ? { target } : {}) });
}

/** 装備ミラー: client が解決した装備 (GearSelection: 強化値つき) をサーバーへ送り、戦闘に反映させる (#377)。 */
export function serverGear(agent: Agent, gear: unknown): Promise<{ ok: true }> {
  return callEdge<{ ok: true }>(agent, LXM_GEAR, '/api/world/gear', { gear });
}

/** しらべる: サーバーがアイテムを判定して gameState 在庫に付与。found (無ければ null) + 新 materials を返す。 */
/** しらべる。`key` は冪等キー — 応答だけ落ちて押し直したときに二重に引かれないため。 */
export interface ServerSearchResult {
  found: string | null;
  materials: Record<string, number>;
  power: number;
  /** 置きアイテムを取ったときだけ (D-STORY-009 / D-CHEST-001)。パワーは減らない。itemId/count は spot のときだけ。 */
  placed?: { kind: PlacedKind; rewards: PlacedReward[]; itemId?: string; count?: number };
  flags?: string[];
  /** 置きアイテムを取ったときだけ: 開けた宝箱・取ったキラキラ。 */
  openedSpots?: string[];
  /** 置きアイテムを取ったときだけ: 装備の個体 (宝箱から装備が出ると増える)。 */
  pieces?: ServerOwnedPiece[];
  /** 無料で何も起きないマス: 開封済みの宝箱 / 鍵。 */
  placedState?: 'empty' | 'locked';
  hint?: string;
  scenarioMessages?: ScenarioMessage[];
}
export function serverSearch(agent: Agent, token?: string, key?: string): Promise<ServerSearchResult> {
  return callEdge<ServerSearchResult>(agent, LXM_SEARCH, '/api/world/search', { ...(token ? { token } : {}), ...(key ? { key } : {}) });
}

/** オンボード用リセット: 権威 gameState + 戦闘ガードをサーバーで削除する (本人のみ)。次の入場で初期状態に戻る。
 *  client 側 PDS レコード (制作/装備/世界/パワー/分析XP) の初期化は呼び出し側 (resetOnboarding) が行う。 */
export function serverReset(agent: Agent): Promise<{ ok: true }> {
  return callEdge<{ ok: true }>(agent, LXM_RESET, '/api/world/reset', {});
}

export interface XpClaimResult { granted: number; jobXp: number; duplicate: boolean; power: number; streakDays: number }

/**
 * 投稿を申告して XP を積む (#534 / #551)。
 *
 * **額は送らない — サーバーが決める。** 送るのは投稿の URI だけで、サーバーが
 * 「実在する / 本人のもの / 新しい」を PDS に問い合わせて確かめてから払う。
 * 同じ投稿の再送は積まれない (冪等)。
 */
export function serverClaimXp(agent: Agent, input: { archetype: string; postUri: string }): Promise<XpClaimResult> {
  return callEdge<XpClaimResult>(agent, LXM_XP_CLAIM, '/api/xp/claim', input);
}

/** **管理者専用**: 自分のジョブ Lv を権威 state に直接セットする (#534)。
 *  XP を一本化した結果、analysis を書き換えるだけではレベルが動かないため。
 *  edge 側が ADMIN_DIDS でゲートする (client の isAdminDid は表示ゲートに過ぎない)。 */
export function serverAdminSetJobLevel(agent: Agent, archetype: string, level: number): Promise<{ jobXp: number; level: number }> {
  return callEdge<{ jobXp: number; level: number }>(agent, LXM_XP_ADMIN_SET, '/api/xp/admin-set', { archetype, level });
}

export interface ServerShopResult {
  power: number;
  materials: Record<string, number>;
  /** 制作したときだけ: サーバーが抽選した強化値。 */
  level?: number;
  /** ひきとりのときだけ: 得たパワー。 */
  powerGained?: number;
  /** 同じ rkey の再送だったか。 */
  duplicate?: boolean;
  /** 操作後の所持個体 (#551 段階 2)。client はこれを正として表示する。 */
  pieces?: ServerOwnedPiece[];
}

/** なんでも屋: 装備を作ってもらう (#551)。**費用も強化値もサーバーが決める**。
 *  client は返ってきた level を craft レコードに記帳するだけ。 */
export function serverShopCraft(agent: Agent, itemId: string, rkey: string, token?: string): Promise<ServerShopResult> {
  // **位置トークンを送る** (#636)。サーバーは token の座標で店を判定する。送らないと
  // state の座標に倒れ、村の中では入口の座標のままなので「店の外」と判定される。
  return callEdge<ServerShopResult>(agent, LXM_SHOP_CRAFT, '/api/shop/craft', { itemId, rkey, ...(token ? { token } : {}) });
}

/** なんでも屋: きたえる (#551 段階 2)。同じ品・同じ強化値の 2 個体 → +1。
 *  消費する個体は**権威側の所持から**探すので、持っていない rkey は通らない。 */
export function serverShopForge(agent: Agent, rkeys: [string, string], rkey: string, token?: string): Promise<ServerShopResult> {
  return callEdge<ServerShopResult>(agent, LXM_SHOP_FORGE, '/api/shop/forge', { rkeys, rkey, ...(token ? { token } : {}) });
}

/**
 * もちもの: 装備を すてる (#575)。
 *
 * **街の外でも呼べる** — 所持上限に達すると制作も購入も断られるので、街に着くまで
 * 整理できないと詰む。**パワーは返らない** (返すと「作る → すてる」でパワーが増える)。
 * まとめて渡せる (1 個ずつだと 100 個の整理で 100 往復になる)。
 */
export function serverShopDiscard(agent: Agent, rkeys: string[], rkey: string): Promise<ServerShopResult> {
  return callEdge<ServerShopResult>(agent, LXM_SHOP_DISCARD, '/api/shop/discard', { rkeys, rkey });
}

export interface ServerQuestResult {
  activeQuests: Array<{ id: string; progress: number }>;
  questsDone?: string[];
  power: number;
  materials: Record<string, number>;
  /** 達成時のみ: 付与された報酬。 */
  rewarded?: { power?: number; itemId?: string; count?: number };
  /** 達成時のみ: きずなの ♥ が増えた仲間と、足したあとの party (D-PARTY-004)。 */
  allyBondUp?: ServerBondUp[];
  party?: ServerParty;
  /** 受注で始まる戦闘 (D-STORY-009)。サーバーが封印済み。 */
  encounter?: ServerEncounter;
  /** 進行フラグ (#545)。サーバーが立てた結果。 */
  flags?: string[];
  /** シナリオのお知らせ (一度だけ)。 */
  notices?: string[];
  /** notices と同じ行 + 演出 (D-STORY-007)。旧 edge は返さない。 */
  scenarioMessages?: ScenarioMessage[];
}

/** シナリオのお知らせ 1 行と、その行に付く演出。 */
export interface ScenarioMessage { text: string; effects?: StoryEffect[]; /** 話す NPC の id (#907)。 */ npcId?: string }

/** 新 edge の scenarioMessages を優先し、旧 edge の文字列だけのお知らせは演出なしで包む。 */
export function scenarioMessagesOf(messages: ScenarioMessage[] | undefined, notices: string[] | undefined): ScenarioMessage[] {
  return messages?.length ? messages : (notices ?? []).map((text) => ({ text }));
}

/** ゲーム内クエスト (#423): 受注。進行は GameState に積まれ、討伐は勝利時にサーバーが数える。 */
export function serverQuestAccept(agent: Agent, questId: string, token?: string): Promise<ServerQuestResult> {
  return callEdge<ServerQuestResult>(agent, LXM_QUEST_ACCEPT, '/api/quest/accept', { questId, ...(token ? { token } : {}) });
}

/** 会話の後の戦闘 (D-STORY-009)。隣にいるか・いまのセリフに戦闘があるかはサーバーが確かめる。 */
export function serverStoryBattle(agent: Agent, npcId: string, token?: string): Promise<ServerEncounter> {
  return callEdge<ServerEncounter>(agent, LXM_STORY_BATTLE, '/api/story/battle', { npcId, ...(token ? { token } : {}) });
}

/** 宿屋に泊まる (#766)。会話で「はい」と答えたときだけ呼ぶ。扉の上にいるか・満タンか・残高は edge が決める。 */
export interface ServerInnRestResult {
  /** 泊まった (paid>0) か、満タンで泊まる必要がなかった (paid=0)。 */
  inn?: { name?: string; paid: number; power: number };
  /** パワーが足りず泊まれなかった。 */
  innDenied?: { name?: string; price: number; power: number };
}
export function serverInnRest(agent: Agent, token?: string): Promise<ServerInnRestResult> {
  return callEdge<ServerInnRestResult>(agent, LXM_INN_REST, '/api/inn/rest', token ? { token } : {});
}

/** ゲーム内クエスト (#423): 達成。**条件も報酬もサーバーが検証・付与する** (collect は素材を引き取られる)。
 *  talk は位置トークンで「相手の隣にいる」をサーバーが確かめる (D-STORY-009)。 */
export function serverQuestComplete(agent: Agent, questId: string, token?: string): Promise<ServerQuestResult> {
  return callEdge<ServerQuestResult>(agent, LXM_QUEST_COMPLETE, '/api/quest/complete', { questId, ...(token ? { token } : {}) });
}

/** なんでも屋: どうぐを 1 個買う (D-ITEM-001)。品揃え・値段・上限はサーバーが決める。rkey で冪等。 */
export function serverShopBuy(agent: Agent, itemId: string, rkey: string, token?: string): Promise<ServerShopResult> {
  return callEdge<ServerShopResult>(agent, LXM_SHOP_BUY, '/api/shop/buy', { itemId, rkey, ...(token ? { token } : {}) });
}

/** なんでも屋: 素材のひきとり (#551)。権威側の在庫と残高を動かす。 */
export function serverShopSell(agent: Agent, materialId: string, count: number, rkey: string, token?: string): Promise<ServerShopResult> {
  return callEdge<ServerShopResult>(agent, LXM_SHOP_SELL, '/api/shop/sell', { materialId, count, rkey, ...(token ? { token } : {}) });
}

/** あおぞらパワーを消費する (#551)。**値段はサーバーが決める** — client が金額を送らない。
 *  `key` は冪等キー (同じ引き直しで二重に引かれない)。 */
export function serverSpendPower(agent: Agent, reason: 'card-draw', key: string): Promise<{ power: number; spent: number; duplicate: boolean }> {
  return callEdge<{ power: number; spent: number; duplicate: boolean }>(agent, LXM_POWER_SPEND, '/api/power/spend', { reason, key });
}

export interface ServerPdsUsage {
  usage: { limit: number | null; remaining: number | null; reset: number | null; policy: string | null; at: number; writes: number } | null;
  /** 残量から計算した「あと何操作できるか」。 */
  opsRemaining: number | null;
  /** 1 操作あたりの point。 */
  pointsPerOp: number;
}

/** **管理者専用**: PDS の書き込みレート残量を見る (#548)。**PDS 分割の潮時の判断材料**。
 *  読み取りなので PDS の point は消費しない。 */
export async function serverPdsUsage(agent: Agent): Promise<ServerPdsUsage> {
  if (!EDGE_URL || !EDGE_DID) throw new WorldServerError('サーバー権威 API 未設定', 0, 'not_configured');
  const token = await serviceToken(agent, LXM_ADMIN_PDS_USAGE);
  const res = await fetch(`${EDGE_URL}/api/admin/pds-usage`, { headers: { authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(EDGE_TIMEOUT_MS) });
  const data = (await res.json().catch(() => ({}))) as ServerPdsUsage & { error?: string };
  if (!res.ok) throw new WorldServerError(data.error ?? `edge ${res.status}`, res.status, data.error);
  return data;
}

/** **管理者専用**: あおぞらパワーを権威 state に付与する。
 *  管理画面のパワー付与は client 側の PDS レコードしか書いておらず、報酬の可否を決める
 *  `GameState.power` は動いていなかった (= 画面にはあるのに勝っても報酬が出ない)。 */
export function serverAdminGrantPower(agent: Agent, amount: number): Promise<{ power: number }> {
  return callEdge<{ power: number }>(agent, LXM_POWER_ADMIN_GRANT, '/api/power/admin-grant', { amount });
}

/** 権威 GameState を読む (表示用: パワー/XP/素材/位置)。GET だが lxm 付き JWT で本人確認。 */
export async function serverState(agent: Agent): Promise<ServerStateResult> {
  if (!EDGE_URL || !EDGE_DID) throw new WorldServerError('サーバー権威 API 未設定', 0, 'not_configured');
  const token = await serviceToken(agent, LXM_STATE);
  let res: Response;
  try {
    res = await fetch(`${EDGE_URL}/api/me/state`, { headers: { authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(EDGE_TIMEOUT_MS) });
  } catch (e) {
    const timedOut = e instanceof DOMException && (e.name === 'TimeoutError' || e.name === 'AbortError');
    throw new WorldServerError(timedOut ? 'サーバーが応答しません' : '通信に失敗しました', 0, timedOut ? 'timeout' : 'network');
  }
  const data = (await res.json().catch(() => ({}))) as ServerStateResult & { error?: string };
  if (!res.ok) {
    if (res.status === 401) tokenCache.delete(LXM_STATE);
    throw new WorldServerError(data.error ?? `edge ${res.status}`, res.status, data.error);
  }
  return data;
}
